import React, { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  OnboardingDraft,
  OnboardingDraftMaintenanceSchedule,
  ValidationIssue,
} from '@intelligent-play-v2/domain';

import { Button, ButtonType } from '~/components/button';
import { TextInput } from '~/components/forms';
import { LabelledCheckbox } from '~/components/labelledCheckbox';

import {
  fieldStateFor,
  inputStateClass,
  issuesAt,
  messageAt,
} from './fieldState';
import { useDraftField } from './useDraftField';

interface OnboardingMaintenanceSchedulesProps {
  pitchIndex: number;
  /** Live validation issues for the whole draft, matched here by field path. */
  issues: ValidationIssue[];
  /** Schedules still identical to the server template, so they can be labelled as such. */
  templateSignatures: string[];
}

const emptySchedule = (today: string): OnboardingDraftMaintenanceSchedule => ({
  type: '',
  startDate: today,
  maintainer: '',
  expectedCompletionTimeMins: null,
  trackedProcedure: false,
  noticePeriodDays: null,
  usageRanges: [{ hours: [0, 25], frequencyPerWeek: 1 }],
});

/** The tracked-procedure flag, registered so ticking it survives to the committed draft. */
const TrackedProcedureCheckbox: FC<{
  pitchIndex: number;
  scheduleIndex: number;
}> = ({ pitchIndex, scheduleIndex }) => {
  const [isTracked, setIsTracked] = useDraftField(
    `pitches.${pitchIndex}.maintenanceSchedules.${scheduleIndex}.trackedProcedure` as const
  );
  return (
    <LabelledCheckbox
      title="Tracked procedure"
      isChecked={isTracked}
      toggleChecked={() => setIsTracked(!isTracked)}
    />
  );
};

/**
 * How often the procedure is needed at each level of weekly usage.
 *
 * The list is bound as one registered field so adding and removing rows actually takes,
 * while the numbers inside each row are ordinary inputs.
 */
const UsageRanges: FC<{
  pitchIndex: number;
  scheduleIndex: number;
  issues: ValidationIssue[];
}> = ({ pitchIndex, scheduleIndex, issues }) => {
  const { control, register } = useFormContext<OnboardingDraft>();
  const base = `pitches.${pitchIndex}.maintenanceSchedules.${scheduleIndex}` as const;
  /*
   * A field array rather than a plain value: removing a row from an index-keyed list of
   * registered inputs leaves the surviving inputs showing the old values while the form
   * state has shifted underneath them, so the reviewer reads one set of frequencies and
   * commits another. `useFieldArray` re-keys the rows for us.
   */
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${base}.usageRanges`,
  });

  return (
    <div>
      <p className="text-sm font-semibold">How often, by weekly usage hours</p>
      <p className="text-sm text-primary-500">
        Each row is a band of weekly play: when a pitch is used from{' '}
        <em>from</em> to <em>to</em> hours in a week, the procedure is needed
        this many times per week. Fractions are normal for the slow ones — 0.115
        a week is about once every nine weeks.
      </p>
      <div className="mt-2 flex flex-col gap-2">
        {fields.map((field, rangeIndex) => {
          const rangeAt = `${base}.usageRanges.${rangeIndex}` as const;
          const rangeIssues = issuesAt(issues, rangeAt);
          return (
            <div key={field.id} className="flex flex-col gap-1">
              <div className="flex items-end gap-2">
                <TextInput
                  label="From (hours)"
                  isLightMode
                  size="small"
                  type="number"
                  register={register(`${rangeAt}.hours.0` as const, {
                    valueAsNumber: true,
                  })}
                  className={inputStateClass(fieldStateFor(issues, rangeAt))}
                />
                <TextInput
                  label="To (hours)"
                  isLightMode
                  size="small"
                  type="number"
                  register={register(`${rangeAt}.hours.1` as const, {
                    valueAsNumber: true,
                  })}
                  className={inputStateClass(fieldStateFor(issues, rangeAt))}
                />
                <TextInput
                  label="Times per week"
                  isLightMode
                  size="small"
                  type="number"
                  register={register(`${rangeAt}.frequencyPerWeek` as const, {
                    valueAsNumber: true,
                  })}
                  className={inputStateClass(
                    fieldStateFor(issues, `${rangeAt}.frequencyPerWeek`)
                  )}
                />
                <Button
                  text="✕"
                  size="xsmall"
                  type={ButtonType.Outline}
                  onClick={() => remove(rangeIndex)}
                />
              </div>
              {rangeIssues.map(issue => (
                <p
                  key={issue.message}
                  className={`text-sm ${
                    issue.severity === 'error' ? 'text-red-dark' : 'text-yellow'
                  }`}
                >
                  {issue.message}
                </p>
              ))}
            </div>
          );
        })}
      </div>
      <div className="mt-2">
        <Button
          text="＋ Add usage range"
          size="xsmall"
          type={ButtonType.Outline}
          onClick={() => append({ hours: [0, 25], frequencyPerWeek: 1 })}
        />
      </div>
    </div>
  );
};

/**
 * The repeating maintenance-schedule section for one pitch.
 *
 * Pre-populated from the server-side template when the form asked for tracking on
 * non-natural turf; a card still matching that template is labelled, so the reviewer can
 * see what they have and have not touched.
 */
export const OnboardingMaintenanceSchedules: FC<OnboardingMaintenanceSchedulesProps> = ({
  pitchIndex,
  issues,
  templateSignatures,
}) => {
  const { control, register, watch } = useFormContext<OnboardingDraft>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `pitches.${pitchIndex}.maintenanceSchedules`,
  });

  const schedules = watch(`pitches.${pitchIndex}.maintenanceSchedules`) ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <p className="text-lg font-semibold">Maintenance schedules</p>
      {fields.length === 0 && (
        <p className="mt-1 text-sm text-primary-500">
          None yet. The form either declined maintenance tracking or the turf is
          natural — add a schedule below if this pitch needs one.
        </p>
      )}

      <div className="mt-3 flex flex-col gap-4">
        {fields.map((field, scheduleIndex) => {
          const base = `pitches.${pitchIndex}.maintenanceSchedules.${scheduleIndex}` as const;
          const schedule = schedules[scheduleIndex];
          const isFromTemplate =
            !!schedule && templateSignatures.includes(JSON.stringify(schedule));

          return (
            <div
              key={field.id}
              className="rounded border border-primary-250 bg-white p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-3">
                  <p className="text-base font-semibold">
                    Schedule {scheduleIndex + 1}
                  </p>
                  {isFromTemplate && (
                    <span className="rounded bg-primary-100 px-2 py-0.5 text-sm text-primary-600">
                      from default template
                    </span>
                  )}
                </div>
                <Button
                  text="Remove"
                  size="xsmall"
                  type={ButtonType.OutlineDanger}
                  onClick={() => remove(scheduleIndex)}
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <TextInput
                  label="Type"
                  isLightMode
                  required
                  register={register(`${base}.type` as const)}
                  className={inputStateClass(
                    fieldStateFor(issues, `${base}.type`)
                  )}
                  errorMessage={messageAt(issues, `${base}.type`)}
                />
                <TextInput
                  label="Maintainer"
                  isLightMode
                  register={register(`${base}.maintainer` as const)}
                />
                <TextInput
                  label="Start date"
                  isLightMode
                  type="date"
                  register={register(`${base}.startDate` as const)}
                  className={inputStateClass(
                    fieldStateFor(issues, `${base}.startDate`)
                  )}
                  errorMessage={messageAt(issues, `${base}.startDate`)}
                />
                <TextInput
                  label="Expected completion (minutes)"
                  isLightMode
                  type="number"
                  register={register(
                    `${base}.expectedCompletionTimeMins` as const,
                    {
                      setValueAs: value =>
                        value === '' ? null : Number(value),
                    }
                  )}
                  className={inputStateClass(
                    fieldStateFor(issues, `${base}.expectedCompletionTimeMins`)
                  )}
                  errorMessage={messageAt(
                    issues,
                    `${base}.expectedCompletionTimeMins`
                  )}
                />
                <TextInput
                  label="Notice period (days)"
                  isLightMode
                  type="number"
                  register={register(`${base}.noticePeriodDays` as const, {
                    setValueAs: value => (value === '' ? null : Number(value)),
                  })}
                  className={inputStateClass(
                    fieldStateFor(issues, `${base}.noticePeriodDays`)
                  )}
                  errorMessage={messageAt(issues, `${base}.noticePeriodDays`)}
                />
                <div className="flex items-end">
                  <TrackedProcedureCheckbox
                    pitchIndex={pitchIndex}
                    scheduleIndex={scheduleIndex}
                  />
                </div>
              </div>

              <div className="mt-4">
                <UsageRanges
                  pitchIndex={pitchIndex}
                  scheduleIndex={scheduleIndex}
                  issues={issues}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* deliberately prominent rather than a text link: adding a schedule is a decision */}
      <div className="mt-4">
        <Button
          text="＋ Add maintenance schedule"
          type={ButtonType.Outline}
          className="w-full"
          onClick={() => append(emptySchedule(today))}
        />
      </div>
    </div>
  );
};
